import React from 'react';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Avatar,
  Chip,
  List,
  ListItem,
  ListItemText,
  Button,
  LinearProgress,
  useTheme,
  alpha,
} from '@mui/material';
import {
  AttachMoney as MoneyIcon,
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  AccountBalance as BankIcon,
  Receipt as ReceiptIcon,
  Warning as WarningIcon,
  CheckCircle as CheckIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, BarChart, Bar } from 'recharts';

const FinanzasDashboard = () => {
  const theme = useTheme();
  const navigate = useNavigate();

  const quickStats = [
    { title: 'Facturación Mensual', value: '$42.5M', change: '+8.2%', positive: true, icon: <MoneyIcon />, color: theme.palette.success.main },
    { title: 'Cuentas por Cobrar', value: '$18.3M', change: '12 facturas', positive: null, icon: <ReceiptIcon />, color: theme.palette.info.main },
    { title: 'Cuentas por Pagar', value: '$9.7M', change: '8 vencidas', positive: false, icon: <TrendingDownIcon />, color: theme.palette.warning.main },
    { title: 'Saldo Bancario', value: '$15.2M', change: '+2.1%', positive: true, icon: <BankIcon />, color: theme.palette.primary.main },
  ];

  const flujoData = [
    { mes: 'Ago', ingresos: 35, egresos: 28 },
    { mes: 'Sep', ingresos: 38, egresos: 30 },
    { mes: 'Oct', ingresos: 42, egresos: 32 },
    { mes: 'Nov', ingresos: 40, egresos: 31 },
    { mes: 'Dic', ingresos: 45, egresos: 35 },
    { mes: 'Ene', ingresos: 42.5, egresos: 33 },
  ];

  const presupuestosObra = [
    { obra: 'Pozo XY-123', presupuesto: 5200000, ejecutado: 4100000, porcentaje: 79 },
    { obra: 'Pozo AB-456', presupuesto: 3800000, ejecutado: 1900000, porcentaje: 50 },
    { obra: 'Workover MZ-789', presupuesto: 1200000, ejecutado: 1380000, porcentaje: 115 },
    { obra: 'Perforación NW-012', presupuesto: 6500000, ejecutado: 1300000, porcentaje: 20 },
  ];

  const vencimientosProximos = [
    { concepto: 'IVA Enero 2026', fecha: '15/02/2026', monto: '$850.000', tipo: 'Impuesto' },
    { concepto: 'Factura Prov. #4521', fecha: '10/02/2026', monto: '$1.250.000', tipo: 'Proveedor' },
    { concepto: 'Ganancias 1er Anticipo', fecha: '20/02/2026', monto: '$2.100.000', tipo: 'Impuesto' },
    { concepto: 'Factura Prov. #4498', fecha: '12/02/2026', monto: '$780.000', tipo: 'Proveedor' },
  ];

  return (
    <Box className="fade-in">
      <Box sx={{ mb: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box>
          <Typography variant="h4" fontWeight={700} color="primary">
            Finanzas
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Gestión financiera integral
          </Typography>
        </Box>
        <Button variant="contained" startIcon={<ReceiptIcon />} onClick={() => navigate('/finanzas/pagos-cobranzas')}>
          Nuevo Movimiento
        </Button>
      </Box>

      {/* Quick Stats */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        {quickStats.map((stat, index) => (
          <Grid item xs={12} sm={6} lg={3} key={index}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                  <Box>
                    <Typography variant="h4" fontWeight={700}>
                      {stat.value}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {stat.title}
                    </Typography>
                    {stat.positive !== null && (
                      <Chip
                        size="small"
                        label={stat.change}
                        icon={stat.positive ? <TrendingUpIcon fontSize="small" /> : <TrendingDownIcon fontSize="small" />}
                        sx={{
                          mt: 1,
                          bgcolor: alpha(stat.positive ? theme.palette.success.main : theme.palette.error.main, 0.1),
                          color: stat.positive ? theme.palette.success.main : theme.palette.error.main,
                        }}
                      />
                    )}
                    {stat.positive === null && (
                      <Chip size="small" label={stat.change} sx={{ mt: 1 }} variant="outlined" />
                    )}
                  </Box>
                  <Avatar sx={{ bgcolor: alpha(stat.color, 0.1), color: stat.color }}>
                    {stat.icon}
                  </Avatar>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      <Grid container spacing={3}>
        {/* Flujo de Caja */}
        <Grid item xs={12} lg={8}>
          <Card sx={{ height: '100%' }}>
            <CardContent>
              <Typography variant="h6" fontWeight={600} gutterBottom>
                Flujo de Caja (Millones ARS)
              </Typography>
              <ResponsiveContainer width="100%" height={300}>
                <AreaChart data={flujoData}>
                  <defs>
                    <linearGradient id="colorIng" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor={theme.palette.success.main} stopOpacity={0.3} />
                      <stop offset="95%" stopColor={theme.palette.success.main} stopOpacity={0} />
                    </linearGradient>
                    <linearGradient id="colorEgr" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor={theme.palette.error.main} stopOpacity={0.3} />
                      <stop offset="95%" stopColor={theme.palette.error.main} stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <XAxis dataKey="mes" />
                  <YAxis />
                  <Tooltip />
                  <Area type="monotone" dataKey="ingresos" stroke={theme.palette.success.main} fill="url(#colorIng)" strokeWidth={2} />
                  <Area type="monotone" dataKey="egresos" stroke={theme.palette.error.main} fill="url(#colorEgr)" strokeWidth={2} />
                </AreaChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </Grid>

        {/* Vencimientos */}
        <Grid item xs={12} lg={4}>
          <Card sx={{ height: '100%' }}>
            <CardContent>
              <Typography variant="h6" fontWeight={600} gutterBottom>
                Próximos Vencimientos
              </Typography>
              <List disablePadding>
                {vencimientosProximos.map((item, index) => (
                  <ListItem
                    key={index}
                    sx={{
                      px: 2,
                      py: 1.5,
                      borderRadius: 2,
                      mb: 1,
                      bgcolor: alpha(theme.palette.warning.main, 0.05),
                    }}
                  >
                    <ListItemText
                      primary={item.concepto}
                      secondary={`${item.fecha} - ${item.monto}`}
                      primaryTypographyProps={{ variant: 'body2', fontWeight: 500 }}
                    />
                    <Chip label={item.tipo} size="small" variant="outlined" />
                  </ListItem>
                ))}
              </List>
            </CardContent>
          </Card>
        </Grid>

        {/* Presupuestos por Obra */}
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Typography variant="h6" fontWeight={600} gutterBottom>
                Control Presupuestario por Obra
              </Typography>
              <Grid container spacing={3}>
                {presupuestosObra.map((obra, index) => (
                  <Grid item xs={12} md={6} key={index}>
                    <Box
                      sx={{
                        p: 2,
                        borderRadius: 2,
                        bgcolor: obra.porcentaje > 100 ? alpha(theme.palette.error.main, 0.05) : alpha(theme.palette.primary.main, 0.02),
                        border: `1px solid ${obra.porcentaje > 100 ? theme.palette.error.main : alpha(theme.palette.divider, 0.5)}`,
                      }}
                    >
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                        <Typography variant="body1" fontWeight={600}>
                          {obra.obra}
                        </Typography>
                        <Chip
                          size="small"
                          label={`${obra.porcentaje}%`}
                          color={obra.porcentaje > 100 ? 'error' : obra.porcentaje > 80 ? 'warning' : 'success'}
                          icon={obra.porcentaje > 100 ? <WarningIcon /> : <CheckIcon />}
                        />
                      </Box>
                      <LinearProgress
                        variant="determinate"
                        value={Math.min(obra.porcentaje, 100)}
                        sx={{
                          height: 8,
                          borderRadius: 4,
                          bgcolor: alpha(theme.palette.grey[400], 0.2),
                          '& .MuiLinearProgress-bar': {
                            bgcolor: obra.porcentaje > 100 ? theme.palette.error.main : obra.porcentaje > 80 ? theme.palette.warning.main : theme.palette.success.main,
                          },
                        }}
                      />
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1 }}>
                        <Typography variant="caption" color="text.secondary">
                          Ejecutado: ${(obra.ejecutado / 1000000).toFixed(2)}M
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Presupuesto: ${(obra.presupuesto / 1000000).toFixed(2)}M
                        </Typography>
                      </Box>
                      {obra.porcentaje > 100 && (
                        <Typography variant="caption" color="error" sx={{ mt: 1, display: 'block' }}>
                          ⚠️ Sobrecosto: Requiere aprobación gerencial
                        </Typography>
                      )}
                    </Box>
                  </Grid>
                ))}
              </Grid>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default FinanzasDashboard;
